import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import { UserMessage } from './user-message';
import { AiMessage } from './ai-message';
import type { ChatTranscriptMessage } from '@/lib/api/chat';

/**
 * T-0100 — Scrollable message list. Auto-scrolls to the bottom on new
 * messages; once the user scrolls up from the bottom, auto-scroll is
 * disabled until they scroll back down (or send a new message —
 * that's the parent's job to call `scrollToBottomOnNextRender()` via
 * the imperative key bump).
 *
 * Why a custom scroll-affinity heuristic instead of just calling
 * `scrollIntoView()` every render: per the ticket §AC ("manual
 * scroll-up disables auto-scroll"), reading an older message should
 * NOT be interrupted by a new turn auto-scrolling them away. We track
 * "is the user pinned to the bottom?" via a 60-px tolerance band and
 * only auto-scroll when that flag is true.
 *
 * The list renders three categories of nodes:
 *   - `messages`: the API-truth turns (persisted in the transcript).
 *   - `optimistic`: optional in-flight user/assistant turns the parent
 *     manages (we can't add to the transcript until the API returns).
 *   - `footer`: optional ReactNode (e.g. typing indicator + cancel
 *     button) — rendered after all messages so it sits at the visible
 *     tail.
 */
export interface MessageListProps {
  engagementId: string;
  messages: readonly ChatTranscriptMessage[];
  /**
   * Optional in-flight optimistic messages — typically the user
   * question (pre-API) and the streaming assistant skeleton.
   */
  optimistic?: ReactNode;
  /**
   * Optional footer (typing indicator, error message, etc.). Rendered
   * AFTER all messages so a scrollIntoView pinned to the bottom edge
   * keeps it visible.
   */
  footer?: ReactNode;
  /**
   * Bump this counter to force a scroll-to-bottom on the next render
   * (e.g. when the user just submitted a new question — we want the
   * input to scroll into view regardless of their current pin).
   */
  scrollToBottomKey?: number;
  className?: string;
}

const SCROLL_BOTTOM_TOLERANCE_PX = 60;

export function MessageList({
  engagementId,
  messages,
  optimistic,
  footer,
  scrollToBottomKey,
  className,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  // Track whether the user has scrolled up from the bottom band.
  // We use scroll affinity (not a one-shot flag) so reattaching at
  // the bottom re-enables auto-scroll without needing to mash a
  // button.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      const node = scrollRef.current;
      if (!node) return;
      const distanceFromBottom = node.scrollHeight - (node.scrollTop + node.clientHeight);
      stickToBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_TOLERANCE_PX;
    }
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll when messages append. Uses useLayoutEffect so the
  // scroll fires *before* paint — avoids a flash of un-scrolled
  // content. Only scrolls when sticky-to-bottom is true (or when the
  // parent imperatively bumped the key).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    // We intentionally depend on messages.length (the row count) +
    // optimistic+footer presence as a coarse signal. Tracking deeper
    // (e.g. content strings) would scroll on every streamed character;
    // the optimistic component owns its own scroll-into-view via the
    // stickToBottom heuristic.
  }, [messages.length, optimistic != null, footer != null]);

  // Imperative scroll-to-bottom — re-pin even if the user had
  // scrolled up.
  useLayoutEffect(() => {
    if (scrollToBottomKey === undefined) return;
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [scrollToBottomKey]);

  return (
    <div
      ref={scrollRef}
      role="log"
      aria-live="polite"
      aria-atomic="false"
      aria-label="Chat transcript"
      className={cn('flex-1 overflow-y-auto px-6 py-6', className)}
    >
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4">
        {messages.map((m) =>
          m.role === 'user' ? (
            <UserMessage key={m.message_id} content={m.content} />
          ) : (
            <AiMessage
              key={m.message_id}
              engagementId={engagementId}
              content={m.content}
              displayContent={m.content}
              confidence={m.confidence ?? null}
              noEvidence={m.no_evidence ?? null}
              tokenCostCents={m.token_cost_cents ?? null}
              citations={m.citations}
            />
          ),
        )}
        {optimistic}
        {footer}
      </div>
    </div>
  );
}
